import { useState, useEffect, useCallback } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { EmailSjabloon } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Loader2, Eye, EyeOff, Code, RotateCcw, Mail } from 'lucide-react';

interface MergeField {
  field: string;
  description: string;
}

/**
 * Een e-mailsjabloon zoals dit scherm het gebruikt.
 *
 * availableMergeFields komt uit de database als jsonb en kan daar alles zijn; deze
 * omzetting maakt er een lijst van of anders een lege lijst. Zonder die stap zou een
 * onverwachte waarde het scherm laten crashen op `.map`.
 */
interface EmailTemplate {
  id: string;
  templateKey: string;
  subject: string;
  bodyHtml: string;
  description: string | null;
  availableMergeFields: MergeField[];
}

export function EmailTemplatesTab() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSource, setShowSource] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { templates: gevonden } = await api.get<{ templates: EmailSjabloon[] }>(
        '/admin/email-templates',
      );

      const omgezet: EmailTemplate[] = gevonden.map((t) => ({
        id: t.id,
        templateKey: t.templateKey,
        subject: t.subject,
        bodyHtml: t.bodyHtml,
        description: t.description,
        availableMergeFields: Array.isArray(t.availableMergeFields)
          ? (t.availableMergeFields as unknown as MergeField[])
          : [],
      }));

      setTemplates(omgezet);
      if (!selectedTemplate && omgezet.length > 0) {
        selectTemplate(omgezet[0]);
      }
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de templates niet laden');
    } finally {
      setIsLoading(false);
    }
    // selectedTemplate hoort hier niet in: dan zou elke selectie een nieuwe
    // ophaalronde starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const selectTemplate = (t: EmailTemplate) => {
    setSelectedTemplate(t);
    setEditSubject(t.subject);
    setEditBody(t.bodyHtml);
    setShowPreview(false);
    setShowSource(false);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setIsSaving(true);
    try {
      // Op template_key en niet op id: de sleutel is wat de route identificeert, en
      // die is stabiel. Een id kan bij een herstel van de database wijzigen.
      await api.patch(`/admin/email-templates/${encodeURIComponent(selectedTemplate.templateKey)}`, {
        subject: editSubject,
        bodyHtml: editBody,
      });
      toast.success('Email template opgeslagen');
      setSelectedTemplate({ ...selectedTemplate, subject: editSubject, bodyHtml: editBody });
      await fetchTemplates();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon template niet opslaan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (selectedTemplate) {
      setEditSubject(selectedTemplate.subject);
      setEditBody(selectedTemplate.bodyHtml);
    }
  };

  const insertMergeField = (field: string) => {
    // Insert into body at cursor or append
    setEditBody((prev) => prev + field);
  };

  const getPreviewHtml = () => {
    let html = editBody;
    const sampleValues: Record<string, string> = {
      '{{praktijknaam}}': 'Huisartsenpraktijk De Linde',
      '{{gebruikersnaam}}': 'Dr. Jansen',
      '{{verificatiecode}}': '123456',
      '{{email}}': 'arts@praktijk.nl',
    };
    for (const [key, val] of Object.entries(sampleValues)) {
      html = html.split(key).join(val);
    }
    return html;
  };

  const templateLabel = (key: string) => {
    const labels: Record<string, string> = {
      verification_email: 'Verificatie E-mail',
    };
    return labels[key] || key;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Templates
        </CardTitle>
        <CardDescription>
          Beheer de e-mail templates die naar gebruikers worden verstuurd. Gebruik merge velden om dynamische content in te voegen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Template selector */}
        {templates.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {templates.map((t) => (
              <Button
                key={t.id}
                variant={selectedTemplate?.id === t.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => selectTemplate(t)}
              >
                {templateLabel(t.templateKey)}
              </Button>
            ))}
          </div>
        )}

        {selectedTemplate && (
          <>
            {/* Description */}
            {selectedTemplate.description && (
              <p className="text-xs text-muted-foreground">{selectedTemplate.description}</p>
            )}

            {/* Merge Fields */}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Beschikbare merge velden</Label>
              <div className="flex gap-1.5 flex-wrap">
                {selectedTemplate.availableMergeFields.map((mf) => (
                  <Badge
                    key={mf.field}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10 transition-colors text-xs"
                    onClick={() => insertMergeField(mf.field)}
                    title={`${mf.description} — klik om in te voegen`}
                  >
                    {mf.field}
                    <span className="ml-1 text-muted-foreground font-normal">({mf.description})</span>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <Label className="text-xs">Onderwerp</Label>
              <Input
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                className="mt-1 text-sm font-mono"
              />
            </div>

            {/* Toggle bar */}
            <div className="flex gap-2">
              <Button
                variant={!showPreview && !showSource ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setShowPreview(false); setShowSource(false); }}
              >
                Editor
              </Button>
              <Button
                variant={showSource ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setShowSource(true); setShowPreview(false); }}
              >
                <Code className="h-3.5 w-3.5 mr-1" />
                HTML Bron
              </Button>
              <Button
                variant={showPreview ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setShowPreview(true); setShowSource(false); }}
              >
                {showPreview ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                Voorbeeld
              </Button>
            </div>

            {/* Body editor / preview */}
            {showPreview ? (
              <div className="border rounded-md overflow-hidden">
                <div className="bg-muted px-3 py-1.5 text-xs text-muted-foreground border-b">
                  Voorbeeld (met voorbeelddata)
                </div>
                <div
                  className="bg-white p-4 min-h-[300px]"
                  dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
                />
              </div>
            ) : showSource ? (
              <div>
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  className="w-full min-h-[400px] font-mono text-xs bg-background border border-input rounded-md px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                  spellCheck={false}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="border rounded-md overflow-hidden">
                  <div className="bg-muted px-3 py-1.5 text-xs text-muted-foreground border-b flex items-center justify-between">
                    <span>Visuele editor</span>
                    <span className="text-[10px]">Klik op merge velden hierboven om in te voegen</span>
                  </div>
                  <div
                    className="bg-white p-4 min-h-[300px] prose prose-sm max-w-none focus:outline-none"
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: editBody }}
                    onBlur={(e) => setEditBody(e.currentTarget.innerHTML)}
                  />
                </div>
              </div>
            )}

            {/* Attachment info */}
            <div className="bg-muted/50 rounded-md p-3 text-xs text-muted-foreground">
              <strong>Bijlage:</strong> De verwerkersovereenkomst PDF wordt automatisch als bijlage meegezonden bij deze email.
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isSaving} className="gap-2">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Opslaan
              </Button>
              <Button variant="outline" onClick={handleReset} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
            </div>
          </>
        )}

        {templates.length === 0 && (
          <p className="text-sm text-muted-foreground">Geen email templates gevonden.</p>
        )}
      </CardContent>
    </Card>
  );
}
